import type { Vec2, PolygonWithHoles, AABB } from '../geo/types';
import type { Layout } from '../data/types';
import type { GroundPiece, SiteModel } from './types';
import { buildWaterUnion, buildFarRing, boundsRing, damsOf, openWaterOf } from './water';
import {
  buildChannelRegions,
  createWaterClassifier,
  type ChannelRegion,
  type WaterClassifier,
} from './classify';
import { buildGroundPartition, bufferAllStreets } from './partition';
import { buildWaterEdges } from './edges';
import { buildBridges, deckFootprint } from './bridges';
import { createSiteQueries, type FarWaterPiece } from './queries';
import type { FarRing } from './farRing';
import { createPolygonIndex, type PolygonIndex } from '../geo/polygonIndex';
import { difference, union, fromRing, simplify } from '../geo/clip';
import { WORLD } from '../core/config';
import { waterAccesses, waterAccessFootprint } from './waterAccess';
import { createGroundElevation } from './groundElevation';
import { bridgeProfile } from './bridgeHeight';
import { damDeckFootprints } from './damDeck';
import { foundationHeight } from '../world/fabric/foundation';

// Far-ring pieces span tens of kilometres, so a fine grid would only multiply their edges.
const FAR_WATER_CELL_M = 1000;

export function buildSiteModel(layout: Layout): SiteModel {
  const { minX, minY, maxX, maxY } = layout.bounds;
  const bounds: AABB = { minX, minY, maxX, maxY };

  const channels = buildChannelRegions(
    layout.water.channels,
    openWaterOf(layout),
    bounds,
    damsOf(layout)
  );
  const water = buildWaterUnion(layout, channels, bounds);
  const waterIndex = createPolygonIndex(water, 20);
  const classifier = createWaterClassifier(layout, channels);

  const cityEnclosure = layout.land.cityEnclosure as Vec2[] | undefined;
  const lastage = layout.land.lastage as Vec2[] | undefined;
  const waterEdges = buildWaterEdges(water, classifier, bounds, cityEnclosure, lastage);

  const bufferedStreets = bufferAllStreets(layout);
  const { streetPolygons, dikePolygons } = bufferedStreets;
  const streetsUnion = union(simplify([...streetPolygons, ...dikePolygons], 0.1));

  const ground = buildGroundPartition(layout, water, bounds, bufferedStreets);
  const farRing = farRingOf(bounds, channels, water, waterIndex, ground);

  const buildable = buildBuildableArea(layout, water, streetsUnion, bounds);
  const buildableIndex = createPolygonIndex(buildable, 20);

  const elevation = createGroundElevation(layout);
  const bridges = buildBridges(layout, water, waterIndex).map((bridge) => {
    const profile = bridgeProfile(bridge);
    const gate =
      bridge.id === 'bridge-way-38316242'
        ? layout.landmarks.find((l) => l.kind === 'waterGate')
        : undefined;
    if (gate) {
      const height = foundationHeight(gate.footprint, elevation);
      return { ...bridge, bankY: [height, height] as const };
    }
    const at = (x: number): Vec2 => [
      bridge.a[0] + profile.axis[0] * x,
      bridge.a[1] + profile.axis[1] * x,
    ];
    return { ...bridge, bankY: [elevation(at(profile.from)), elevation(at(profile.to))] as const };
  });
  const queries = createSiteQueries({
    layout,
    bridges,
    bounds,
    waterIndex,
    farWaterIndex: createPolygonIndex(farRing.water, FAR_WATER_CELL_M),
    classifier,
    deckIndex: createPolygonIndex(deckFootprint(bridges), 20),
    farWater: farWaterOf(farRing, classifier),
  });

  return {
    waterAccessDecks: waterAccesses(layout).map(waterAccessFootprint),
    damDecks: damDeckFootprints(layout.landmarks),
    bounds,
    water,
    farRing: { land: farRing.land, water: farRing.water },
    waterEdges,
    ground,
    streets: streetsUnion,
    buildable,
    bridges,
    waterIndex,
    buildableIndex,
    queries,
  };
}

function farRingOf(
  bounds: AABB,
  channels: readonly ChannelRegion[],
  water: readonly PolygonWithHoles[],
  waterIndex: PolygonIndex,
  ground: readonly GroundPiece[]
): FarRing {
  const crossings = channels.flatMap(({ channel, exits }) =>
    exits.map((exit) => ({ channel, ...exit }))
  );
  return buildFarRing(boundsRing(bounds), (p) => waterIndex.contains(p), WORLD.farRingRadius, {
    water,
    land: ground.map((piece) => piece.polygon),
    crossings,
  });
}

// A river band carries on its channel; other far water takes the class of what it continues.
function farWaterOf(farRing: FarRing, classifier: WaterClassifier): readonly FarWaterPiece[] {
  return farRing.waterRuns.map(({ polygon, channel, probe }) => ({
    polygon,
    waterClass: channel ? { channel, kind: channel.kind } : classifier.classify(probe),
  }));
}

function buildBuildableArea(
  layout: Layout,
  water: readonly PolygonWithHoles[],
  streets: readonly PolygonWithHoles[],
  _bounds: AABB
): readonly PolygonWithHoles[] {
  const cityPoly: PolygonWithHoles[] = [];
  const cityEnclosure = layout.land.cityEnclosure as Vec2[] | undefined;
  const lastage = layout.land.lastage as Vec2[] | undefined;

  if (cityEnclosure && cityEnclosure.length >= 3) {
    cityPoly.push(fromRing(cityEnclosure));
  }
  if (lastage && lastage.length >= 3) {
    cityPoly.push(fromRing(lastage));
  }

  if (cityPoly.length === 0) {
    return [];
  }

  const combined = union(cityPoly);
  let buildable = simplify(difference(combined, water), 0.1);
  buildable = simplify(difference(buildable, streets), 0.1);
  // Dry ground kept in the water (layout water.dams) is a gate's ground or a bank: no lot takes it.
  const dams = damsOf(layout);
  if (dams.length > 0) buildable = simplify(difference(buildable, dams), 0.1);

  const squares: PolygonWithHoles[] = [];
  for (const sq of layout.squares) {
    const ring = sq.polygon as Vec2[] | undefined;
    if (ring && ring.length >= 3) {
      squares.push(fromRing(ring));
    }
  }
  if (squares.length > 0) {
    buildable = simplify(difference(buildable, squares), 0.1);
  }

  const gardens: PolygonWithHoles[] = [];
  for (const g of layout.gardens) {
    const ring = g.polygon as unknown as Vec2[] | undefined;
    if (ring && ring.length >= 3) {
      gardens.push(fromRing(ring));
    }
  }
  if (gardens.length > 0) {
    buildable = simplify(difference(buildable, gardens), 0.1);
  }

  return buildable;
}
